import {
  type PostDocumentMutationType,
  usePatchDocument,
  usePatchDocumentArchive,
  usePostDocumentQueueResume,
  usePutDocumentsSignaturePosition,
} from 'entities/document/api';
import {
  useDeleteDocumentAttachment,
  useDeleteDocumentExternalFilesDelete,
  usePostDocumentAttachment,
} from 'entities/document/api/hooks';
import {
  type PostPdfsDocumentType,
  usePostPdfs2,
} from 'entities/document/api/pdfs';
import { useDuplicateDocument } from 'features/document-duplicate';
import { useRemoveNote, useUpdateNote } from 'features/document-note';
import { useTranslation } from 'react-i18next';
import type { PostDocumentAttachmentRequestType } from '../../entities/document/api';
import { confirmDeleteDocument } from '../../features/document-delete/widgets';
import { downloadFile } from '../../helpers';
import { useShowNotification } from '../useShowNotification';
import { useUser } from '../useUser';

export type UseDocumentActionsProps = {
  documentId?: number;
  versionId?: number;
};

/**
 * Custom hook to expose actions on a document.
 */
export const useDocumentActions = (props: UseDocumentActionsProps) => {
  const { t } = useTranslation('documents');
  const { data: user } = useUser();
  const showNotification = useShowNotification();

  /**
   * Hook to download a document.
   */
  const { mutateAsync: downloadMutation } = usePostPdfs2();

  /**
   * Hook to update the document.
   */
  const { mutateAsync: updateDocumentMutation } = usePatchDocument(
    props.documentId,
  );

  /**
   * Hook to resume the document queue.
   */
  const { mutateAsync: resumeMutation } = usePostDocumentQueueResume();
  const { updateNote } = useUpdateNote(props.documentId);
  const { removeNote } = useRemoveNote(props.documentId);

  const { duplicate } = useDuplicateDocument(props.documentId);

  /**
   * Hook to archive a document.
   */
  const { mutateAsync: archiveMutation } = usePatchDocumentArchive();

  /**
   * Hook to add attachment to document
   */
  const { mutateAsync: _addAttachment } = usePostDocumentAttachment();

  /**
   * Hook to delete attachment from document
   */
  const { mutateAsync: _deleteAttachment } = useDeleteDocumentAttachment();

  /**
   * Hook to delete file attachment from document
   */
  const { mutateAsync: _deleteFileAttachment } =
    useDeleteDocumentExternalFilesDelete();

  /**
   * Hook to update the position of the signature.
   */
  const { mutateAsync: updatePositionSignature } =
    usePutDocumentsSignaturePosition({
      documentId: props.documentId!,
      userId: user?.id,
    });

  /**
   * Resume the document queue.
   */
  const resume = () => resumeMutation(props.documentId!);

  /**
   * Archive a document.
   * @param event - The event object.
   */
  const archive = async (event: React.SyntheticEvent | undefined) => {
    event?.stopPropagation();

    if (!props.documentId) {
      // eslint-disable-next-line no-console
      console.warn('Document ID is required to archive.');
    }

    await archiveMutation({
      documentId: props.documentId!,
    });

    showNotification({
      message: t('documents:notification.archived'),
    });
  };

  /**
   * Updates the document with the provided data.
   * @param data - The data to update the document with.
   */
  const updateDocument = async (data: PostDocumentMutationType) =>
    updateDocumentMutation(data);

  /**
   * Download a document.
   * @param document - The document to download.
   */
  const download = async (document: PostPdfsDocumentType) => {
    const defaultDocument: Omit<PostPdfsDocumentType, 'id'> = {
      excludedAttachmentsIds: [],
      excludedExternalFilesIds: [],
      renderBaseContract: true,
      renderCertificate: true,
    };

    const { response } = await downloadMutation({
      documents: [
        {
          ...defaultDocument,
          ...document,
        },
      ],
    });

    if (response && response.type === 'DOWNLOAD' && response?.urls.length > 0) {
      response.urls.forEach(({ url }) => {
        downloadFile(url);
      });
    }
  };

  /**
   * Add attachment to document
   * @param attachment
   */
  const addAttachment = async (
    attachment: Omit<PostDocumentAttachmentRequestType, 'documentId'>,
  ) => {
    if (!props.documentId) {
      throw new Error(
        'Document ID is required to add attachment to  document.',
      );
    }

    return _addAttachment({
      documentId: props.documentId!,
      ...attachment,
    });
  };

  /**
   * Delete attachment from document
   * @param attachmentId
   */
  const deleteAttachment = async (attachmentId: number) =>
    _deleteAttachment({
      documentId: props.documentId!,
      attachmentId,
    });

  /**
   * Delete file attachment from document
   * @param attachmentId
   */
  const deleteFileAttachment = async (attachmentId: number) => {
    await _deleteFileAttachment({
      documentId: props.documentId!,
      attachmentId,
    });
  };

  /**
   * Delete document
   */
  const deleteDocument = async () => {
    if (props.documentId) {
      return confirmDeleteDocument(props.documentId);
    }

    return false;
  };

  return {
    /**
     * Archive a document.
     */
    archive,

    /**
     * Download a document.
     */
    download,

    /**
     * Duplicates a document.
     */
    duplicate,

    /**
     * Updates the note of the document.
     */
    updateNote,

    /**
     * Removes the note of the document.
     */
    removeNote,

    /**
     * Updates the document with the provided data.
     * @param data - The data to update the document with.
     * @returns {Promise<void>}
     */
    updateDocument,

    /**
     * Updates the position of the signature.
     */
    updatePositionSignature,

    /**
     * Resumes the document queue.
     * @returns {Promise<void>}
     */
    resume,

    /**
     * Add attachment to document
     */
    addAttachment,

    /**
     * Delete document
     */
    deleteDocument,

    /**
     * Delete attachment from document
     */
    deleteAttachment,

    /**
     * Delete file attachment from document
     */
    deleteFileAttachment,
  };
};
